import { User } from "firebase/auth";
import { toast } from "sonner";

export interface AuthenticationState {
  isAuthenticated: boolean;
  lastTokenRefresh: Date | null;
  tokenExpirationTime: Date | null;
  hasRoleClaim: boolean;
  tokenValid: boolean;
}

/**
 * Checks if token is close to expiring (less than 15 minutes)
 */
export const isTokenExpiring = (expirationTime: Date | null): boolean => {
  if (!expirationTime) return false;
  // Less than 15 minutes remaining
  return (expirationTime.getTime() - Date.now()) < 15 * 60 * 1000;
};

/**
 * Decodes a JWT token
 */
export const decodeJwt = (token: string) => {
  try {
    // JWT structure is xxxxx.PAYLOAD.xxxxx
    // Split the token and get the middle part (payload)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * Refreshes the Firebase auth token
 */
export const refreshFirebaseAuthToken = async (user: User | null): Promise<string | null> => {
  if (!user) {
    console.log("No user to refresh token for");
    return null;
  }
  
  try {
    console.log("Refreshing auth token for", user.email);
    
    // First reload the user to ensure we have the latest auth state
    await user.reload();
    console.log("User reloaded from Firebase");
    
    // Force token refresh to get latest custom claims
    await user.getIdToken(true);
    
    // Get the refreshed token
    const token = await user.getIdToken();
    
    // Get the decoded token to check claims
    const refreshedDecodedToken = await user.getIdTokenResult();
    console.log("Token refreshed with claims:", refreshedDecodedToken.claims);
    
    return token;
  } catch (error) {
    console.error("Error refreshing auth token:", error instanceof Error ? error.message : String(error));
    return null;
  }
};

/**
 * Gets the current Firebase auth token
 */
export const getCurrentAuthToken = async (user: User | null, forceRefresh = false): Promise<string | null> => {
  if (!user) return null;
  
  try {
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error("Error getting current auth token:", error);
    return null;
  }
};
